import { finishMemberSetup, requestMemberSetupCode } from "@/lib/auth.functions";
import { supabase } from "@/integrations/supabase/client";

export type MemberSignupInput = {
  email: string;
  password: string;
  fullName: string;
  emailRedirectTo?: string;
  /** Required when claiming an admin-created member account. */
  code?: string;
};

export class MemberSignupAlreadyClaimedError extends Error {
  constructor() {
    super("This account is already set up. Sign in or use Forgot password.");
    this.name = "MemberSignupAlreadyClaimedError";
  }
}

/** Claim path needs the emailed setup code before finishMemberSetup. */
export class MemberSignupCodeRequiredError extends Error {
  constructor() {
    super("Enter the setup code we emailed you to finish setting up this account.");
    this.name = "MemberSignupCodeRequiredError";
  }
}

function isExistingAccountError(message: string) {
  return /already registered|already exists|user already/i.test(message);
}

export async function attemptMemberSignup({
  email,
  password,
  fullName,
  emailRedirectTo = window.location.origin,
  code,
}: MemberSignupInput) {
  const { error: signUpError } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { full_name: fullName }, emailRedirectTo },
  });

  if (!signUpError) {
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    if (!signInError) return { created: true as const };
  } else if (!isExistingAccountError(signUpError.message)) {
    throw signUpError;
  }

  const trimmedCode = code?.trim();
  if (!trimmedCode) {
    await requestMemberSetupCode({ data: { email } });
    throw new MemberSignupCodeRequiredError();
  }

  try {
    await finishMemberSetup({ data: { email, password, fullName, code: trimmedCode } });
  } catch (err) {
    const message = (err as Error).message;
    if (/already set up/i.test(message)) {
      throw new MemberSignupAlreadyClaimedError();
    }
    throw err;
  }

  const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
  if (signInError) throw signInError;

  return { created: false as const, claimed: true as const };
}
